/**
 * Bank Statement Import Modal — Sprint 4 of the rent-payment-tracking work.
 *
 * Lets a landlord drag in a CSV export from their bank. The backend parses
 * it, detects the format (Monzo / Starling / Revolut / Wise / Barclays /
 * HSBC / Lloyds / NatWest / Nationwide / Anna / Tide / Generic), matches
 * each credit against the LP-XXXX-NN payment ref, and records the payment.
 *
 * Marketing line: "Two minutes a month — drag your statement in, every
 * paid tenant turns green automatically."
 */

import React, { useState, useRef } from 'react';
import { paymentsService } from '../../services/api';

interface ImportStatementModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete?: () => void;
}

interface ImportSummary {
    format: string;
    statementHash: string;
    totalRows: number;
    credits: number;
    matched: number;
    queued: number;
    duplicates: number;
    errors: number;
    skippedRows: number;
}

interface ImportDetail {
    rowIndex: number;
    paidDate?: string;
    amount?: number;
    payerName?: string;
    outcome: 'matched' | 'queued' | 'duplicate' | 'error';
    tenantId?: string | null;
    paymentRef?: string | null;
    reason?: string | null;
    error?: string;
}

export default function ImportStatementModal({ isOpen, onClose, onComplete }: ImportStatementModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [summary, setSummary] = useState<ImportSummary | null>(null);
    const [details, setDetails] = useState<ImportDetail[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const handleFile = (f: File | null) => {
        setError(null);
        setSummary(null);
        setDetails([]);
        setFile(f);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError(null);
        try {
            const res = await paymentsService.importStatement(file);
            setSummary(res.data.summary);
            setDetails(res.data.details || []);
            onComplete?.();
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || 'Import failed');
        } finally {
            setUploading(false);
        }
    };

    const reset = () => {
        setFile(null);
        setSummary(null);
        setDetails([]);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden border border-gray-200 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 flex items-center">
                            <i className="fas fa-file-csv text-emerald-600 mr-3"></i>
                            Import Bank Statement
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">Auto-match payments to your tenants using their LP-XXXX-NN reference</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition" title="Close">
                        <i className="fas fa-times text-lg"></i>
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {/* Step 1 — upload */}
                    {!summary && (
                        <>
                            <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl mb-6">
                                <div className="flex">
                                    <i className="fas fa-info-circle text-emerald-600 mr-3 mt-1"></i>
                                    <div className="text-sm text-emerald-800">
                                        <p className="font-bold mb-1">Supported banks</p>
                                        <p className="text-xs">
                                            Monzo, Starling, Revolut, Wise, Barclays, HSBC, Lloyds/Halifax, NatWest/RBS,
                                            Nationwide, Anna, Tide — and any other UK bank with a standard CSV export.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragOver(false);
                                    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
                                }}
                                className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${
                                    dragOver ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/30'
                                }`}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,text/csv"
                                    onChange={(e) => handleFile(e.target.files?.[0] || null)}
                                    className="sr-only"
                                />
                                <i className={`fas fa-cloud-upload-alt text-5xl mb-4 ${dragOver ? 'text-emerald-600' : 'text-gray-300'}`}></i>
                                <h4 className="text-base font-bold text-gray-900">
                                    {file ? file.name : 'Drag your bank statement here'}
                                </h4>
                                <p className="text-xs text-gray-500 mt-1">
                                    {file ? `${(file.size / 1024).toFixed(1)} KB` : 'or click to browse — .csv only, max 10 MB'}
                                </p>
                                {!file && (
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
                                    >
                                        Browse files
                                    </button>
                                )}
                            </div>

                            {error && (
                                <div className="mt-4 bg-red-50 border border-red-100 p-3 rounded-lg flex items-start">
                                    <i className="fas fa-exclamation-triangle text-red-600 mr-3 mt-0.5"></i>
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            )}

                            <div className="flex justify-between items-center mt-6 pt-4 border-t">
                                <span className="text-xs text-gray-400">Re-uploading the same file is safe — duplicates are detected automatically.</span>
                                <button
                                    onClick={handleUpload}
                                    disabled={!file || uploading}
                                    className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow"
                                >
                                    {uploading ? (
                                        <><i className="fas fa-spinner fa-spin mr-2"></i>Processing…</>
                                    ) : (
                                        <>Start import <i className="fas fa-arrow-right ml-2"></i></>
                                    )}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Step 2 — result */}
                    {summary && (
                        <>
                            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 p-5 rounded-xl mb-6 flex items-center">
                                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white mr-4">
                                    <i className="fas fa-check text-xl"></i>
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-bold text-gray-900">Statement processed</h4>
                                    <p className="text-xs text-gray-600">Detected format: <span className="font-mono font-semibold">{summary.format}</span></p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                <StatCard label="Total rows" value={summary.totalRows} color="gray" />
                                <StatCard label="Credits"    value={summary.credits}    color="blue" />
                                <StatCard label="Matched"    value={summary.matched}    color="emerald" icon="fa-check-circle" />
                                <StatCard label="Queued"     value={summary.queued}     color="amber"   icon="fa-clock" />
                                <StatCard label="Duplicates" value={summary.duplicates} color="indigo"  icon="fa-copy" />
                                <StatCard label="Errors"     value={summary.errors}     color="red"     icon="fa-exclamation" />
                                <StatCard label="Skipped"    value={summary.skippedRows} color="gray" />
                            </div>

                            {details.length > 0 && (
                                <div className="border border-gray-100 rounded-xl overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600">Per-row outcome ({details.length} rows)</div>
                                    <div className="max-h-64 overflow-y-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-gray-50 text-left text-gray-500">
                                                <tr>
                                                    <th className="px-3 py-2">Date</th>
                                                    <th className="px-3 py-2">Amount</th>
                                                    <th className="px-3 py-2">Payer</th>
                                                    <th className="px-3 py-2">Outcome</th>
                                                    <th className="px-3 py-2">Ref</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {details.map((d) => (
                                                    <tr key={d.rowIndex} className="hover:bg-gray-50">
                                                        <td className="px-3 py-1.5">{d.paidDate || '—'}</td>
                                                        <td className="px-3 py-1.5 font-mono">{d.amount != null ? `£${d.amount.toLocaleString()}` : '—'}</td>
                                                        <td className="px-3 py-1.5 truncate max-w-xs">{d.payerName || '—'}</td>
                                                        <td className="px-3 py-1.5"><OutcomeBadge outcome={d.outcome} /></td>
                                                        <td className="px-3 py-1.5 font-mono text-gray-400">{d.paymentRef || d.reason || ''}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {summary.queued > 0 && (
                                <div className="mt-4 bg-amber-50 border border-amber-100 p-3 rounded-lg flex items-start">
                                    <i className="fas fa-clock text-amber-600 mr-3 mt-0.5"></i>
                                    <div className="text-sm text-amber-900">
                                        <p className="font-bold">{summary.queued} payment{summary.queued === 1 ? '' : 's'} couldn't be matched</p>
                                        <p className="text-xs mt-0.5">They've been added to the Pending Match queue. Open it from Super Admin → Payments to review.</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center mt-6 pt-4 border-t">
                                <button onClick={reset} className="text-sm text-emerald-700 font-medium hover:underline">
                                    <i className="fas fa-arrow-left mr-1"></i>Upload another statement
                                </button>
                                <button onClick={onClose} className="bg-gray-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-gray-800">
                                    Done
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, color, icon }: { label: string; value: number; color: string; icon?: string }) {
    const colors: Record<string, string> = {
        gray:    'bg-gray-50 text-gray-700 border-gray-200',
        blue:    'bg-blue-50 text-blue-700 border-blue-200',
        emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        amber:   'bg-amber-50 text-amber-700 border-amber-200',
        indigo:  'bg-indigo-50 text-indigo-700 border-indigo-200',
        red:     'bg-red-50 text-red-700 border-red-200',
    };
    return (
        <div className={`p-3 rounded-xl border ${colors[color] || colors.gray}`}>
            <div className="text-[10px] uppercase font-black tracking-wider opacity-70 flex items-center gap-1">
                {icon && <i className={`fas ${icon}`}></i>}
                <span>{label}</span>
            </div>
            <div className="text-xl font-bold mt-1">{value.toLocaleString()}</div>
        </div>
    );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
    const styles: Record<string, string> = {
        matched:   'bg-emerald-100 text-emerald-700',
        queued:    'bg-amber-100 text-amber-700',
        duplicate: 'bg-indigo-100 text-indigo-700',
        error:     'bg-red-100 text-red-700',
    };
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${styles[outcome] || 'bg-gray-100 text-gray-600'}`}>
            {outcome}
        </span>
    );
}
